import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AGENDA_COLUMNS } from './agendaConfig';
import { API_BASE_URL } from './constants';
import { agendaDateKey } from './agendaPresentation';
import { chainIntervalOverlaps, parseChainBlockedIntervals, sharedChainBlockedIntervals, type ChainBlockedInterval } from './chainIntraDayConstraints';
import { agendaMoveCoveringInterval, agendaMoveDateTime, agendaMoveFitsDay, agendaMoveOverlaps, type AgendaMoveTarget } from './agendaMove';
import type { AgendaMoveCheck } from './useAgendaAppointmentMove';
import type { AgendaAppointmentInfoData } from './components/AgendaAppointmentInfo';

type DraftAppointment = {
  id: string; when?: string; duration: number; procedureIds: string[]; practitionerId: string;
  appointmentInfoMap: Record<string, unknown>; description?: string;[key: string]: unknown;
};
export type AgendaChainDraft = {
  version: 1; updatedAt: string; startDate: string; endDate: string;
  manualPlacement: true; editingIndex: number; editingProcedureId: string; editingProcedureName: string;
  selectedPatientId: string; selectedPatient?: { firstName: string; familyName: string } | null;
  chainAppointments: DraftAppointment[]; proposedDates?: string[];[key: string]: unknown;
};
const storageKey = 'currentAppointmentChain';
const headers = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token') || ''}` });
const keyFor = (date: string, provider: string) => `${date}|${provider}`;
const targetKey = (target: AgendaMoveTarget) => `${keyFor(target.date, target.practitionerId)}|${target.minute}`;
const waiting: AgendaMoveCheck = { status: 'loading', message: 'Verfügbarkeit und Min./Max.-Tage werden geprüft…' };
const invalid = (message: string): AgendaMoveCheck => ({ status: 'invalid', message });

export function readAgendaChainPlacement(): AgendaChainDraft | null {
  try {
    const draft = JSON.parse(localStorage.getItem(storageKey) || 'null');
    return draft?.version === 1 && draft.manualPlacement === true && Number.isInteger(draft.editingIndex)
      && Array.isArray(draft.chainAppointments) && draft.chainAppointments[draft.editingIndex]?.id === draft.editingProcedureId
      ? draft : null;
  } catch { return null; }
}

type DayRule = { status: 'loading' | 'ready' | 'error'; allowed: boolean; message: string; blockedIntervals?: ChainBlockedInterval[] };
type CalendarAppointment = { id: string; when: string; duration: number; practitionerUserId: string };

export function useAgendaChainPlacement({ draft, visibleDates, providers, appointments, loading, loadError, colorForProcedure, onReturn }: {
  draft: AgendaChainDraft | null;
  visibleDates: readonly Date[];
  providers: readonly string[];
  appointments: readonly CalendarAppointment[];
  loading: boolean;
  loadError: string;
  colorForProcedure: (id?: string) => NonNullable<AgendaAppointmentInfoData['color']>;
  onReturn: () => void;
}) {
  const [dragging, setDragging] = useState(false);
  const [hoverTarget, setHoverTarget] = useState<AgendaMoveTarget | null>(null);
  const [pendingTarget, setPendingTarget] = useState<AgendaMoveTarget | null>(null);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [days, setDays] = useState<Record<string, DayRule>>({});
  const [checks, setChecks] = useState<Record<string, AgendaMoveCheck>>({});
  const dayCache = useRef(new Map<string, Promise<DayRule>>());
  const timeCache = useRef(new Map<string, Promise<number[]>>());
  const checksCache = useRef(new Map<string, Promise<AgendaMoveCheck>>());
  const active = useRef(true), request = useRef(0), savingRef = useRef(false);
  const initialStored = useRef(localStorage.getItem(storageKey));
  const returnRef = useRef(onReturn); returnRef.current = onReturn;
  useEffect(() => { active.current = true; return () => { active.current = false; request.current += 1; }; }, []);
  const cancel = useCallback(() => { if (!savingRef.current) { request.current += 1; returnRef.current(); } }, []);
  const appointment = draft?.chainAppointments[draft.editingIndex];
  const moving = useMemo(() => !draft || !appointment ? null : ({
    practitionerId: appointment.practitionerId,
    info: {
      id: appointment.id, when: appointment.when || '', duration: appointment.duration,
      patientName: [draft.selectedPatient?.firstName, draft.selectedPatient?.familyName].filter(Boolean).join(' ') || 'Patient noch nicht ausgewählt',
      title: draft.editingProcedureName || 'Behandlung noch nicht ausgewählt', description: appointment.description || '',
      practitionerLabel: AGENDA_COLUMNS.find((column) => column.practitionerId === appointment.practitionerId)?.label || '',
      color: colorForProcedure(appointment.procedureIds?.[0]),
    } as AgendaAppointmentInfoData,
  }), [draft, appointment, colorForProcedure]);

  const loadDays = (dates: readonly Date[], practitionerId: string, fresh = false): Promise<DayRule>[] => {
    if (!draft) return [];
    const missing = dates.filter((date) => fresh || !dayCache.current.has(keyFor(agendaDateKey(date), practitionerId)));
    if (missing.length) {
      const keys = missing.map((date) => keyFor(agendaDateKey(date), practitionerId));
      setDays((current) => ({ ...current, ...Object.fromEntries(keys.map((key) => [key, { status: 'loading', allowed: false, message: waiting.message }])) }));
      const promise = (async (): Promise<DayRule[]> => {
        try {
          if (!localStorage.getItem('token')) throw new Error('Anmeldung fehlt');
          const response = await fetch(`${API_BASE_URL}/appointments/draft-date-and-time-infos`, // was: draft-date-infos
            {
              method: 'POST', headers: headers(), body: JSON.stringify({
                editingIndex: draft.editingIndex,
                startDate: `${agendaDateKey(missing[0])}T00:00:00`, endDate: `${agendaDateKey(missing[missing.length - 1])}T00:00:00`,
                chainAppointments: draft.chainAppointments.map((item, index) => index === draft.editingIndex ? { ...item, practitionerId } : item),
                officeId: localStorage.getItem('selectedOfficeId'),
              })
            });
          if (!response.ok) throw new Error('Tagesprüfung fehlgeschlagen');
          const infos: unknown = await response.json();
          // Request a contiguous calendar range; weekends may occur between cached days.
          const first = new Date(`${agendaDateKey(missing[0])}T12:00:00`);
          const last = new Date(`${agendaDateKey(missing[missing.length - 1])}T12:00:00`);
          let count = 1; for (const day = new Date(first); day < last; day.setDate(day.getDate() + 1)) count += 1;
          if (!Array.isArray(infos) || infos.length !== count || infos.some((info) => typeof info !== 'string' || !info.trim())) throw new Error('Ungültige Tagesprüfung');
          return missing.map((date) => {
            const day = new Date(first); let index = 0;
            while (agendaDateKey(day) < agendaDateKey(date)) { day.setDate(day.getDate() + 1); index += 1; }
            const info = infos[index] as string;
            const blockedIntervals = parseChainBlockedIntervals(info);
            const hasIntraDayIntervals = blockedIntervals !== null;
            const [from, to] = info.split('|').map((part) => part.trim());
            return {
              status: 'ready', blockedIntervals: blockedIntervals ?? [], allowed: info === 'Y' || hasIntraDayIntervals, message: info === 'Y' || hasIntraDayIntervals ? ''
                : from && to ? `Ausserhalb der Min./Max.-Tage: zulässig ${from} bis ${to}.` : 'An diesem Tag ist kein zulässiger Termin verfügbar.'
            };
          });
        } catch { return missing.map(() => ({ status: 'error', allowed: false, message: 'Die Serienregeln konnten nicht geprüft werden. Bitte erneut versuchen.' })); }
      })();
      keys.forEach((key, index) => {
        const itemPromise = promise.then((rules) => {
          const result = rules[index];
          if (active.current && dayCache.current.get(key) === itemPromise) setDays((current) => ({ ...current, [key]: result }));
          return result;
        });
        dayCache.current.set(key, itemPromise);
      });
    }
    return dates.map((date) => dayCache.current.get(keyFor(agendaDateKey(date), practitionerId))!);
  };

  useEffect(() => {
    if (draft) providers.forEach((id) => { loadDays(visibleDates, id); });
    // Caches are scoped to the immutable draft; only navigation adds days/providers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, visibleDates, providers]);

  const localCheck = (target: AgendaMoveTarget): AgendaMoveCheck | null => {
    if (!draft || !appointment) return invalid('Kein Serientermin ausgewählt.');
    if (!draft.selectedPatientId || !appointment.procedureIds?.length || !draft.editingProcedureName) return invalid('Bitte zuerst Patient und Behandlung im Serienformular auswählen.');
    if (!Number.isInteger(appointment.duration) || !agendaMoveFitsDay(target, appointment.duration)) return invalid('Die gesamte Termindauer muss zwischen 06:00 und 19:00 Uhr liegen.');
    if (loading) return waiting;
    if (loadError) return invalid('Die Agenda konnte nicht vollständig geladen werden.');
    const provider = AGENDA_COLUMNS.find((column) => column.practitionerId === target.practitionerId);
    if (!target.candidates.includes(target.practitionerId) || !provider?.practitionerUserId) return invalid('Bitte einen Behandler aus dem gewählten Filter auswählen.');
    if (agendaMoveOverlaps(target, appointment.duration, '', provider.practitionerUserId, appointments)) return invalid('Dieser Zeitraum ist bereits belegt.');
    const start = new Date(agendaMoveDateTime(target)).getTime(), end = start + appointment.duration * 60000;
    if (draft.chainAppointments.some((item, index) => index !== draft.editingIndex && item.practitionerId === target.practitionerId && item.when
      && new Date(item.when).getTime() < end && new Date(item.when).getTime() + item.duration * 60000 > start)) return invalid('Dieser Zeitraum überschneidet sich mit einem anderen Termin der Serie.');
    return null;
  };
  const loadTimes = (target: AgendaMoveTarget, duration: number, fresh: boolean) => {
    const key = `${keyFor(target.date, target.practitionerId)}|${duration}`;
    if (!fresh && timeCache.current.has(key)) return timeCache.current.get(key)!;
    const promise = (async (): Promise<number[]> => {
      if (!localStorage.getItem('token')) throw new Error('Anmeldung fehlt');
      const response = await fetch(`${API_BASE_URL}/appointments/possible`, {
        method: 'POST', headers: headers(), body: JSON.stringify({
          patientId: draft!.selectedPatientId, procedureId: appointment!.procedureIds[0], practitionerId: target.practitionerId,
          duration: String(duration), date: `${target.date}T11:12:10`,
        })
      });
      if (!response.ok) throw new Error('Zeitprüfung fehlgeschlagen');
      const slots: unknown = await response.json();
      if (!Array.isArray(slots) || slots.some((slot) => !Number.isInteger(slot) || slot < 0 || slot >= 1440)) throw new Error('Ungültige Zeitprüfung');
      return slots;
    })();
    timeCache.current.set(key, promise); return promise;
  };
  const blockedCheck = (target: AgendaMoveTarget, day: DayRule): AgendaMoveCheck | null =>
    day.blockedIntervals?.some((interval) => chainIntervalOverlaps(target.minute, appointment!.duration, interval))
      ? invalid('Zu dieser Uhrzeit nicht verfügbar') : null;
  const validate = (target: AgendaMoveTarget, fresh = false): Promise<AgendaMoveCheck> => {
    const key = targetKey(target);
    if (!fresh && checksCache.current.has(key)) return checksCache.current.get(key)!;
    setChecks((current) => ({ ...current, [key]: waiting }));
    const promise = (async () => {
      const local = localCheck(target); if (local) return local;
      try {
        const day = await loadDays([new Date(`${target.date}T12:00:00`)], target.practitionerId, fresh)[0];
        if (!day.allowed) return invalid(day.message);
        const blocked = blockedCheck(target, day); if (blocked) return blocked;
        const slots = await loadTimes(target, appointment!.duration, fresh);
        if (!slots.includes(target.minute)) {
          const interval = agendaMoveCoveringInterval(slots, target.minute, appointment!.duration);
          if (!interval || !(await loadTimes(target, interval.duration, fresh)).includes(interval.anchor)) return invalid('Das Backend bestätigt keinen freien Zeitraum für die vollständige Termindauer.');
        }
        return { status: 'valid', message: 'Zulässiger freier Zeitraum.' } as AgendaMoveCheck;
      } catch { return invalid('Die Verfügbarkeit konnte nicht zuverlässig geprüft werden. Bitte erneut versuchen.'); }
    })().then((result) => {
      if (active.current && checksCache.current.get(key) === promise) setChecks((current) => ({ ...current, [key]: result }));
      // A loading agenda must be retried after its appointments arrive.
      if (result.status === 'loading') checksCache.current.delete(key);
      return result;
    });
    checksCache.current.set(key, promise); return promise;
  };
  const check = (target: AgendaMoveTarget): AgendaMoveCheck => {
    if (!target.practitionerId) {
      const results = target.candidates.map((id) => check({ ...target, practitionerId: id }));
      return results.find((result) => result.status === 'valid') || results.find((result) => result.status === 'loading') || results[0] || invalid('Bitte einen Behandlerfilter wählen.');
    }
    const local = localCheck(target); if (local) return local;
    const day = days[keyFor(target.date, target.practitionerId)];
    if (!day || day.status === 'loading') return waiting;
    if (!day.allowed) return invalid(day.message);
    const blocked = blockedCheck(target, day); if (blocked) return blocked;
    return checks[targetKey(target)] || waiting;
  };
  useEffect(() => {
    const target = pendingTarget || hoverTarget;
    if (!draft || !target) return;
    const timer = window.setTimeout(() => {
      (target.practitionerId ? [target.practitionerId] : target.candidates).forEach((id) => { void validate({ ...target, practitionerId: id }); });
    }, 100);
    return () => window.clearTimeout(timer);
    // Per-target promises deduplicate pointer events; local collision checks always run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, hoverTarget, pendingTarget, loading, loadError, appointments]);

  const startDragging = () => { request.current += 1; setDragging(true); setNotice(''); setError(''); };
  const drop = async (target: AgendaMoveTarget | null) => {
    const currentRequest = ++request.current;
    setDragging(false); setHoverTarget(null);
    if (!target || !draft) return;
    setNotice(waiting.message);
    const results = await Promise.all((target.practitionerId ? [target.practitionerId] : target.candidates).map((id) => validate({ ...target, practitionerId: id }, true)));
    if (!active.current || request.current !== currentRequest) return;
    if (!results.some((result) => result.status === 'valid')) { setNotice(results[0]?.message || 'Bitte einen Behandlerfilter wählen.'); return; }
    setNotice(''); setError(''); setPendingTarget(target);
  };
  const confirm = async () => {
    if (!draft || !pendingTarget?.practitionerId || savingRef.current) return;
    savingRef.current = true; setSaving(true); setError('');
    try {
      const result = await validate(pendingTarget, true);
      if (!active.current) return;
      const local = localCheck(pendingTarget);
      if (result.status !== 'valid' || local) throw new Error(local?.message || result.message);
      if (localStorage.getItem(storageKey) !== initialStored.current) throw new Error('Der Serienentwurf wurde zwischenzeitlich geändert. Bitte öffnen Sie ihn erneut.');
      const { editingIndex, editingProcedureId, manualPlacement, editingProcedureName, ...rest } = draft;
      const revised = {
        ...rest, updatedAt: new Date().toISOString(),
        chainAppointments: draft.chainAppointments.map((item, index) => index === editingIndex
          ? { ...item, when: agendaMoveDateTime(pendingTarget), practitionerId: pendingTarget.practitionerId } : item),
        proposedDates: draft.proposedDates?.map((value, index) => index === editingIndex ? '' : value),
      };
      localStorage.setItem(storageKey, JSON.stringify(revised));
      returnRef.current();
    } catch (cause) { if (active.current) setError(cause instanceof Error ? cause.message : 'Die Platzierung konnte nicht übernommen werden.'); }
    finally { savingRef.current = false; if (active.current) setSaving(false); }
  };
  const dateStatus = (date: Date, candidates: readonly string[]) => {
    const rules = candidates.map((id) => days[keyFor(agendaDateKey(date), id)]);
    const allowed = rules.some((rule) => rule?.allowed);
    return { disabled: !allowed, message: allowed ? '' : rules.find((rule) => rule?.status === 'ready')?.message || rules.find((rule) => rule?.status === 'error')?.message || waiting.message };
  };
  const blockedIntervals = (date: Date, candidates: readonly string[]) => {
    const rules = candidates.map((id) => days[keyFor(agendaDateKey(date), id)]);
    if (rules.some((rule) => !rule || rule.status !== 'ready')) return [];
    return sharedChainBlockedIntervals(rules.filter((rule) => rule.allowed).map((rule) => rule.blockedIntervals ?? []));
  };
  return {
    moving, dragging, hoverTarget, pendingTarget, saving, notice, error, cancel, check, startDragging, drop, confirm,
    setDragging, setHoverTarget, setPendingTarget, setError, dateStatus, blockedIntervals
  };
}
